import type { ReactNode } from 'react';

import { useSearch } from '@tanstack/react-router';
import clsx from 'clsx';

import type { FleetMemberRow } from 'src/api/endpoints/cloudEnterprise.types';
import {
  Drawer,
  DrawerContent,
  DrawerPortal,
  DrawerTrigger,
} from 'src/components/Drawer';

import { ButtonClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { fleetMemberRowHelpers } from './fleetMemberRowHelpers';
import { MemberDrawer } from './MemberDrawer';

export function TableDrawer({
  children,
  row,
  open,
  onOpenChange,
  ...rest
}: {
  children: ReactNode;
  row: FleetMemberRow;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const search = useSearch({ strict: false });

  if (fleetMemberRowHelpers.isInvitation(row)) {
    return children;
  }

  return (
    <Drawer
      open={open}
      modal={false}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={true}
      onOpenChange={onOpenChange}
    >
      <DrawerTrigger
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'ArrowRight') {
            if (search.member !== row.data.email) {
              e.stopPropagation();
              e.preventDefault();
              tracker.trackEvent(
                new ButtonClicked({
                  component_location:
                    ComponentLocations.ENTERPRISE.MEMBERS_PAGE,
                  text: '[member table row]',
                  action: 'open member details drawer',
                })
              );
              void onOpenChange?.(true);
            }
          }
        }}
        role="button"
        tabIndex={0}
        className={clsx('ring-neutral-light-8 ring-inset hover:bg-paper-3', {
          'z-10 bg-neutral-light-3': search.member === row.data.email,
        })}
        asChild
        {...rest}
      >
        {children}
      </DrawerTrigger>
      <DrawerPortal>
        <DrawerContent className="w-full max-w-[530px]">
          <MemberDrawer
            data={row.data}
            closeMemberDrawer={() => onOpenChange?.(false)}
          />
        </DrawerContent>
      </DrawerPortal>
    </Drawer>
  );
}
